import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import BusinessLayout from '../../components/business/BusinessLayout';
import Breadcrumb from '../../components/common/Breadcrumb';
import { 
  fetchBusinessProfile, 
  updateBusinessBasicInfo,
  updateBusinessContact,
  updateBusinessHours,
  changeBusinessPassword
} from '../../api/business';

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────
const formatDate = (dateStr) => {
  if (!dateStr) return '–';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const COUNTRY_OPTIONS = [
  { value: 'India', label: '🇮🇳 India' },
  { value: 'USA', label: '🇺🇸 USA' },
  { value: 'UK', label: '🇬🇧 UK' },
  { value: 'Canada', label: '🇨🇦 Canada' },
  { value: 'Australia', label: '🇦🇺 Australia' },
];

// ─────────────────────────────────────────────
// Toast Notification
// ─────────────────────────────────────────────
const Toast = ({ toast }) => {
  if (!toast) return null;
  const isSuccess = toast.type === 'success';
  return (
    <div
      className={`fixed top-5 right-5 z-50 flex items-center gap-3 
        rounded-xl border px-4 py-3 shadow-lg text-[13px] font-medium
        transition-all duration-300
        ${isSuccess
          ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
          : 'bg-red-50 border-red-200 text-red-800'
        }`}
    >
      {isSuccess ? (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ) : (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      )}
      {toast.message}
    </div>
  );
};

// ─────────────────────────────────────────────
// Form Field Component
// ─────────────────────────────────────────────
const Field = ({ label, error, children, hint, required }) => (
  <div>
    <label className="block text-[12px] font-medium text-slate-600 mb-1.5">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
    {hint && !error && <p className="mt-1 text-[11px] text-slate-400">{hint}</p>}
    {error && <p className="mt-1 text-[11px] text-red-500">{error}</p>}
  </div>
);

const inputCls = (err) =>
  `w-full px-3 py-2 rounded-lg border text-[13px] text-slate-800 
   bg-white placeholder:text-slate-300 outline-none transition
   disabled:bg-slate-50 disabled:cursor-not-allowed
   focus:ring-1
   ${err
     ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
     : 'border-slate-200 focus:border-blue-400 focus:ring-blue-100'
   }`;

const selectCls = `w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] text-slate-800 
  bg-white outline-none transition focus:border-blue-400 focus:ring-1 focus:ring-blue-100`;

const textareaCls = (err) =>
  `w-full px-3 py-2 rounded-lg border text-[13px] text-slate-800 
   bg-white placeholder:text-slate-300 outline-none transition resize-none
   focus:ring-1
   ${err
     ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
     : 'border-slate-200 focus:border-blue-400 focus:ring-blue-100'
   }`;

// ─────────────────────────────────────────────
// Status Badge
// ─────────────────────────────────────────────
const StatusBadge = ({ status, type }) => {
  let bgColor = 'bg-amber-100 text-amber-700';
  let displayText = 'Pending';

  if (type === 'verification') {
    if (status === 'verified') {
      bgColor = 'bg-emerald-100 text-emerald-700';
      displayText = '✅ Verified';
    } else if (status === 'pending') {
      bgColor = 'bg-amber-100 text-amber-700';
      displayText = '⏳ Pending';
    } else if (status === 'rejected') {
      bgColor = 'bg-red-100 text-red-700';
      displayText = '❌ Rejected';
    }
  } else if (type === 'approval') {
    if (status) {
      bgColor = 'bg-emerald-100 text-emerald-700';
      displayText = '✅ Approved';
    } else {
      bgColor = 'bg-amber-100 text-amber-700';
      displayText = '⏳ Pending';
    }
  }

  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-semibold ${bgColor}`}>
      {displayText}
    </span>
  );
};

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────
const BusinessProfilePage = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');

  // ─────────────────────────────────────────────
  // Form States
  // ─────────────────────────────────────────────
  const [basicForm, setBasicForm] = useState({
    business_name: '',
    business_category: '',
    business_description: '',
    website_url: '',
  });
  const [basicErrors, setBasicErrors] = useState({});

  const [contactForm, setContactForm] = useState({
    email_business: '',
    phone_number: '',
    street_address: '',
    city: '',
    state_province: '',
    postal_code: '',
    country: '',
  });
  const [contactErrors, setContactErrors] = useState({});

  const [hoursForm, setHoursForm] = useState({});

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordErrors, setPasswordErrors] = useState({});

  // ─────────────────────────────────────────────
  // Load Profile
  // ─────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = await fetchBusinessProfile();
        setProfile(data);

        setBasicForm({
          business_name: data.business_name || '',
          business_category: data.business_category || '',
          business_description: data.business_description || '',
          website_url: data.website_url || '',
        });

        setContactForm({
          email_business: data.email_business || '',
          phone_number: data.phone_number || '',
          street_address: data.street_address || '',
          city: data.city || '',
          state_province: data.state_province || '',
          postal_code: data.postal_code || '',
          country: data.country || '',
        });

        const hours = data.opening_hours || {};
        const defaultHours = {};
        DAY_KEYS.forEach((day) => {
          defaultHours[day] = hours[day] || { open: '09:00', close: '18:00', closed: false };
        });
        setHoursForm(defaultHours);
      } catch (err) {
        setError(err?.response?.data?.message || 'Failed to load profile');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // ─────────────────────────────────────────────
  // Validators
  // ─────────────────────────────────────────────
  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePhone = (phone) => /^\+?[\d\s\-()]{10,}$/.test(phone);
  const validateUrl = (url) => {
    if (!url) return true;
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  // ─────────────────────────────────────────────
  // Save Handlers
  // ─────────────────────────────────────────────
  const handleSaveBasicInfo = async () => {
    const errors = {};
    if (!basicForm.business_name) errors.business_name = 'Business name is required';
    if (!basicForm.business_category) errors.business_category = 'Category is required';
    if (basicForm.website_url && !validateUrl(basicForm.website_url)) {
      errors.website_url = 'Invalid URL format';
    }

    if (Object.keys(errors).length > 0) {
      setBasicErrors(errors);
      return;
    }

    try {
      setSaving(true);
      await updateBusinessBasicInfo(basicForm);
      setToast({ type: 'success', message: 'Basic information updated!' });
      setBasicErrors({});
    } catch (err) {
      setToast({ type: 'error', message: err?.response?.data?.message || 'Failed to update' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveContactInfo = async () => {
    const errors = {};
    if (!contactForm.email_business) errors.email_business = 'Email is required';
    else if (!validateEmail(contactForm.email_business)) errors.email_business = 'Invalid email';

    if (!contactForm.phone_number) errors.phone_number = 'Phone is required';
    else if (!validatePhone(contactForm.phone_number)) errors.phone_number = 'Invalid phone';

    if (!contactForm.street_address) errors.street_address = 'Street address is required';
    if (!contactForm.city) errors.city = 'City is required';
    if (!contactForm.country) errors.country = 'Country is required';

    if (Object.keys(errors).length > 0) {
      setContactErrors(errors);
      return;
    }

    try {
      setSaving(true);
      await updateBusinessContact(contactForm);
      setToast({ type: 'success', message: 'Contact information updated!' });
      setContactErrors({});
    } catch (err) {
      setToast({ type: 'error', message: err?.response?.data?.message || 'Failed to update' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveHours = async () => {
    try {
      setSaving(true);
      await updateBusinessHours(hoursForm);
      setToast({ type: 'success', message: 'Business hours updated!' });
    } catch (err) {
      setToast({ type: 'error', message: err?.response?.data?.message || 'Failed to update' });
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    const errors = {};
    if (!passwordForm.currentPassword) errors.currentPassword = 'Current password is required';
    if (!passwordForm.newPassword) errors.newPassword = 'New password is required';
    if (passwordForm.newPassword.length < 6) errors.newPassword = 'Must be at least 6 characters';
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    if (Object.keys(errors).length > 0) {
      setPasswordErrors(errors);
      return;
    }

    try {
      setSaving(true);
      await changeBusinessPassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      setToast({ type: 'success', message: 'Password changed!' });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordErrors({});
    } catch (err) {
      setToast({ type: 'error', message: err?.response?.data?.message || 'Failed to change password' });
    } finally {
      setSaving(false);
    }
  };

  // ─────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────
  if (loading) {
    return (
      <BusinessLayout title="Profile">
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
        </div>
      </BusinessLayout>
    );
  }

  if (error) {
    return (
      <BusinessLayout title="Profile">
        <Breadcrumb items={[{ label: 'Dashboard', href: '/business/dashboard' }, { label: 'Profile', href: '/business/profile' }]} />
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-[13px]">{error}</div>
      </BusinessLayout>
    );
  }

  return (
    <BusinessLayout title="Profile">
      <Breadcrumb 
        items={[
          { label: 'Dashboard', href: '/business/dashboard' },
          { label: 'Profile', href: '/business/profile' }
        ]} 
      />

      <Toast toast={toast} />

      <div className="mb-6">
        <h2 className="text-[20px] font-bold text-slate-900 mb-1">Business Profile</h2>
        <p className="text-[13px] text-slate-600">Manage your business information, contact details, and settings</p>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white rounded-xl border border-slate-200 mb-6 overflow-hidden">
        <div className="flex border-b border-slate-200">
          {[
            { id: 'basic', label: '📝 Basic Info' },
            { id: 'contact', label: '📞 Contact' },
            { id: 'hours', label: '⏰ Hours' },
            { id: 'password', label: '🔐 Password' },
            { id: 'status', label: '✅ Status' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 px-4 py-3 text-[13px] font-medium transition-colors text-center
                ${activeTab === tab.id
                  ? 'text-blue-600 border-b-2 border-blue-500 bg-blue-50'
                  : 'text-slate-600 hover:text-slate-900'
                }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Basic Info */}
          {activeTab === 'basic' && (
            <div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <Field label="Business Name" required error={basicErrors.business_name}>
                  <input
                    type="text"
                    value={basicForm.business_name}
                    onChange={(e) => setBasicForm({ ...basicForm, business_name: e.target.value })}
                    className={inputCls(basicErrors.business_name)}
                    placeholder="Your Business Name"
                  />
                </Field>

                <Field label="Category" required error={basicErrors.business_category}>
                  <select
                    value={basicForm.business_category}
                    onChange={(e) => setBasicForm({ ...basicForm, business_category: e.target.value })}
                    className={selectCls}
                  >
                    <option value="">Select Category</option>
                  </select>
                </Field>
              </div>

              <div className="mb-4">
                <Field label="Website URL" error={basicErrors.website_url} hint="https://example.com">
                  <input
                    type="url"
                    value={basicForm.website_url}
                    onChange={(e) => setBasicForm({ ...basicForm, website_url: e.target.value })}
                    className={inputCls(basicErrors.website_url)}
                    placeholder="https://your-business.com"
                  />
                </Field>
              </div>

              <div className="mb-6">
                <Field label="Description" hint="Max 500 characters">
                  <textarea
                    value={basicForm.business_description}
                    onChange={(e) => setBasicForm({ ...basicForm, business_description: e.target.value.slice(0, 500) })}
                    className={textareaCls(false)}
                    placeholder="Tell customers about your business..."
                    rows="4"
                  />
                  <div className="text-[11px] text-slate-400 mt-1">{basicForm.business_description.length}/500</div>
                </Field>
              </div>

              <button
                onClick={handleSaveBasicInfo}
                disabled={saving}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg text-[13px] font-medium hover:bg-blue-700 disabled:opacity-50 transition"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}

          {/* Contact Info */}
          {activeTab === 'contact' && (
            <div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <Field label="Email" required error={contactErrors.email_business}>
                  <input
                    type="email"
                    value={contactForm.email_business}
                    onChange={(e) => setContactForm({ ...contactForm, email_business: e.target.value })}
                    className={inputCls(contactErrors.email_business)}
                    placeholder="business@example.com"
                  />
                </Field>

                <Field label="Phone" required error={contactErrors.phone_number}>
                  <input
                    type="tel"
                    value={contactForm.phone_number}
                    onChange={(e) => setContactForm({ ...contactForm, phone_number: e.target.value })}
                    className={inputCls(contactErrors.phone_number)}
                    placeholder="+91 XXXXX XXXXX"
                  />
                </Field>
              </div>

              <div className="mb-4">
                <Field label="Street Address" required error={contactErrors.street_address}>
                  <input
                    type="text"
                    value={contactForm.street_address}
                    onChange={(e) => setContactForm({ ...contactForm, street_address: e.target.value })}
                    className={inputCls(contactErrors.street_address)}
                    placeholder="123 Business Street"
                  />
                </Field>
              </div>

              <div className="grid grid-cols-3 gap-4 mb-4">
                <Field label="City" required error={contactErrors.city}>
                  <input
                    type="text"
                    value={contactForm.city}
                    onChange={(e) => setContactForm({ ...contactForm, city: e.target.value })}
                    className={inputCls(contactErrors.city)}
                    placeholder="New York"
                  />
                </Field>

                <Field label="State/Province">
                  <input
                    type="text"
                    value={contactForm.state_province}
                    onChange={(e) => setContactForm({ ...contactForm, state_province: e.target.value })}
                    className={inputCls(false)}
                    placeholder="NY"
                  />
                </Field>

                <Field label="Postal Code">
                  <input
                    type="text"
                    value={contactForm.postal_code}
                    onChange={(e) => setContactForm({ ...contactForm, postal_code: e.target.value })}
                    className={inputCls(false)}
                    placeholder="10001"
                  />
                </Field>
              </div>

              <div className="mb-6">
                <Field label="Country" required error={contactErrors.country}>
                  <select
                    value={contactForm.country}
                    onChange={(e) => setContactForm({ ...contactForm, country: e.target.value })}
                    className={selectCls}
                  >
                    <option value="">Select Country</option>
                    {COUNTRY_OPTIONS.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </Field>
              </div>

              <button
                onClick={handleSaveContactInfo}
                disabled={saving}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg text-[13px] font-medium hover:bg-blue-700 disabled:opacity-50 transition"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}

          {/* Hours */}
          {activeTab === 'hours' && (
            <div>
              <div className="space-y-3 mb-6">
                {DAY_KEYS.map((dayKey, idx) => {
                  const day = DAYS_OF_WEEK[idx];
                  const dayHours = hoursForm[dayKey] || { open: '09:00', close: '18:00', closed: false };

                  return (
                    <div key={dayKey} className="flex items-center gap-4 pb-3 border-b border-slate-100 last:border-0">
                      <label className="w-24 text-[13px] font-medium text-slate-700">{day}</label>
                      
                      <div className="flex items-center gap-2 flex-1">
                        <input
                          type="checkbox"
                          checked={dayHours.closed}
                          onChange={(e) => setHoursForm({
                            ...hoursForm,
                            [dayKey]: { ...dayHours, closed: e.target.checked }
                          })}
                          className="rounded border-slate-300 cursor-pointer"
                        />
                        <span className="text-[12px] text-slate-600">Closed</span>
                      </div>

                      {!dayHours.closed && (
                        <div className="flex items-center gap-2 flex-1">
                          <input
                            type="time"
                            value={dayHours.open}
                            onChange={(e) => setHoursForm({
                              ...hoursForm,
                              [dayKey]: { ...dayHours, open: e.target.value }
                            })}
                            className="px-3 py-2 rounded-lg border border-slate-200 text-[13px] w-20 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100"
                          />
                          <span className="text-slate-400 text-[13px]">to</span>
                          <input
                            type="time"
                            value={dayHours.close}
                            onChange={(e) => setHoursForm({
                              ...hoursForm,
                              [dayKey]: { ...dayHours, close: e.target.value }
                            })}
                            className="px-3 py-2 rounded-lg border border-slate-200 text-[13px] w-20 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <button
                onClick={handleSaveHours}
                disabled={saving}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg text-[13px] font-medium hover:bg-blue-700 disabled:opacity-50 transition"
              >
                {saving ? 'Saving...' : 'Save Hours'}
              </button>
            </div>
          )}

          {/* Password */}
          {activeTab === 'password' && (
            <div className="max-w-md">
              <Field label="Current Password" required error={passwordErrors.currentPassword}>
                <input
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                  className={inputCls(passwordErrors.currentPassword)}
                  placeholder="Enter current password"
                />
              </Field>

              <div className="mt-4">
                <Field label="New Password" required error={passwordErrors.newPassword} hint="At least 6 characters">
                  <input
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                    className={inputCls(passwordErrors.newPassword)}
                    placeholder="Enter new password"
                  />
                </Field>
              </div>

              <div className="mt-4 mb-6">
                <Field label="Confirm Password" required error={passwordErrors.confirmPassword}>
                  <input
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                    className={inputCls(passwordErrors.confirmPassword)}
                    placeholder="Confirm new password"
                  />
                </Field>
              </div>

              <button
                onClick={handleChangePassword}
                disabled={saving}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg text-[13px] font-medium hover:bg-blue-700 disabled:opacity-50 transition"
              >
                {saving ? 'Updating...' : 'Update Password'}
              </button>
            </div>
          )}

          {/* Status */}
          {activeTab === 'status' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-[12px] font-semibold text-slate-600 uppercase tracking-wider mb-3">Verification</p>
                  <StatusBadge status={profile?.verification_status} type="verification" />
                </div>

                <div>
                  <p className="text-[12px] font-semibold text-slate-600 uppercase tracking-wider mb-3">Approval</p>
                  <StatusBadge status={profile?.is_approved} type="approval" />
                </div>
              </div>

              <div className="border-t border-slate-200 pt-6">
                <h4 className="text-[13px] font-semibold text-slate-900 mb-4">Account Timeline</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-[12px]">
                    <span className="text-slate-600">Created</span>
                    <span className="text-slate-900 font-medium">{formatDate(profile?.created_at)}</span>
                  </div>
                  <div className="flex items-center justify-between text-[12px]">
                    <span className="text-slate-600">Last Modified</span>
                    <span className="text-slate-900 font-medium">{formatDate(profile?.updated_at)}</span>
                  </div>
                </div>
              </div>

              <div className="rounded-lg bg-blue-50 border border-blue-200 p-4">
                <p className="text-[12px] text-blue-700">
                  Status fields are managed by admins. Contact support if you need assistance.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </BusinessLayout>
  );
};

export default BusinessProfilePage;
